import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { banners } from '@/mocks/comics';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % banners.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const banner = banners[current];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      {banners.map((b, i) => (
        <div
          key={b.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={b.image}
            alt={b.title}
            className="w-full h-full object-cover object-center"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-background-950/40 via-background-950/20 to-background-950" />

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-[1400px] mx-auto w-full px-4 md:px-6">
          <div className="max-w-xl">
            <p className="text-primary-400 text-sm md:text-base font-medium mb-2 md:mb-3 animate-[fadeInUp_0.6s_ease-out]">
              {banner.subtitle}
            </p>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-4 md:mb-6 leading-tight animate-[fadeInUp_0.6s_ease-out_0.1s]">
              {banner.title}
            </h2>
            <Link
              to={`/comic/${banner.comicId}`}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-400 text-background-950 font-semibold px-6 py-3 rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer animate-[fadeInUp_0.6s_ease-out_0.2s]"
            >
              立即观看
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {banners.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === current
                ? 'bg-primary-500 w-6'
                : 'bg-foreground-500/50 hover:bg-foreground-400/70'
            }`}
            aria-label={`切换到第${i + 1}张横幅`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + banners.length) % banners.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-950/50 hover:bg-background-950/70 backdrop-blur-sm text-foreground-300 hover:text-primary-400 transition-all cursor-pointer z-10"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-950/50 hover:bg-background-950/70 backdrop-blur-sm text-foreground-300 hover:text-primary-400 transition-all cursor-pointer z-10"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}